/**
 * Response Transformation
 * 
 * Utilities for detecting and transforming legacy API response formats
 * to the new standardized format.
 * 
 * **New Format (API_STANDARD.md):**
 * ```typescript
 * {
 *   success: true,
 *   payload: T,
 *   message?: string,
 *   meta?: { timestamp, requestId, ... }
 * }
 * ```
 * 
 * **Legacy Formats:**
 * 1. Paginated: `{ items: T[], total: number, page: number, limit: number }`
 * 2. Data wrapper: `{ data: T, success?: boolean, message?: string }`
 * 3. Direct data: `T` (no wrapper)
 * 
 * @module responseTransform
 */

import type {
  ApiResponse,
  ApiSuccessResponse,
  LegacyPaginatedResponse,
  LegacyDataResponse,
} from '../types';
import { IS_PRODUCTION } from '../constants';

/**
 * Check if object has specific keys
 */
function hasKeys<T extends string>(
  obj: unknown,
  keys: T[]
): obj is Record<T, unknown> {
  if (!obj || typeof obj !== 'object') {
    return false;
  }
  
  return keys.every(key => key in obj);
}

/**
 * Detect if response is in new format
 * 
 * New format has both 'success' and 'payload' properties.
 * 
 * @param json - Parsed JSON response
 * @returns true if response is in new format
 */
export function isNewFormat(json: unknown): json is ApiResponse {
  return hasKeys(json, ['success', 'payload']);
}

/**
 * Detect if response is legacy paginated format
 * 
 * Paginated format has 'items', 'total', 'page', and 'limit'.
 * 
 * @param json - Parsed JSON response
 * @returns true if response is paginated format
 */
export function isLegacyPaginated(json: unknown): json is LegacyPaginatedResponse<unknown> {
  return hasKeys(json, ['items', 'total']);
}

/**
 * Detect if response is legacy data wrapper format
 * 
 * Data wrapper has 'data' property but NOT 'payload'.
 * 
 * @param json - Parsed JSON response
 * @returns true if response is data wrapper format
 */
export function isLegacyDataWrapper(json: unknown): json is LegacyDataResponse<unknown> {
  return hasKeys(json, ['data']) && !hasKeys(json, ['payload']);
}

/**
 * Detect if response is in legacy format
 * 
 * Checks for:
 * 1. Paginated format (items + total)
 * 2. Data wrapper format (data)
 * 3. Direct data (no wrapper properties)
 * 
 * @param json - Parsed JSON response
 * @returns true if response needs transformation
 * 
 * @example
 * ```typescript
 * isLegacyResponse({ success: true, payload: [] }); // false (new format)
 * isLegacyResponse({ items: [], total: 10 }); // true (paginated)
 * isLegacyResponse({ data: { id: 1 } }); // true (data wrapper)
 * isLegacyResponse({ id: 1, name: 'Test' }); // true (direct data)
 * ```
 */
export function isLegacyResponse(json: unknown): boolean {
  if (!json || typeof json !== 'object') {
    return false;
  }

  const obj = json as Record<string, unknown>;

  // Already in new format
  if (isNewFormat(json)) {
    if (!IS_PRODUCTION) {
      console.debug('[ApiClient] ✅ Response is in NEW format (has success & payload)');
    }
    return false;
  }

  // Legacy paginated response
  if (isLegacyPaginated(json)) {
    if (!IS_PRODUCTION) {
      console.debug('[ApiClient] 🔄 Detected LEGACY paginated response (items + total)');
    }
    return true;
  }

  // Legacy data wrapper
  if (isLegacyDataWrapper(json)) {
    if (!IS_PRODUCTION) {
      console.debug('[ApiClient] 🔄 Detected LEGACY data wrapper response (data)');
    }
    return true;
  }

  // Check if it looks like an error response (don't transform)
  if (('error' in obj || 'message' in obj) && !('success' in obj)) {
    // It's an error, not legacy data
    if (!IS_PRODUCTION) {
      console.debug('[ApiClient] ❌ Response looks like error, not transforming');
    }
    return false;
  }

  // Legacy direct data (no wrapper properties)
  if (!('success' in obj) && !('payload' in obj) && !('data' in obj) && !('items' in obj)) {
    if (!IS_PRODUCTION) {
      console.debug('[ApiClient] 🔄 Detected LEGACY direct data response', Object.keys(obj));
    }
    return true;
  }

  // Default: not legacy
  if (!IS_PRODUCTION) {
    console.debug('[ApiClient] ⚠️ Response does NOT match any legacy pattern, treating as new format');
  }
  return false;
}

/**
 * Transform legacy response to new ApiResponse format
 * 
 * Handles all three legacy formats:
 * 1. Paginated: `{ items: [], total: N }` → `{ success: true, payload: { items: [], total: N } }`
 * 2. Data wrapper: `{ data: T }` → `{ success: true, payload: T }`
 * 3. Direct data: `T` → `{ success: true, payload: T }`
 * 
 * @param json - Parsed JSON response in legacy format
 * @returns Standardized ApiSuccessResponse
 * 
 * @example
 * ```typescript
 * // Paginated
 * transformLegacyResponse({ items: [1, 2, 3], total: 3 });
 * // { success: true, payload: { items: [1, 2, 3], total: 3 } }
 * 
 * // Data wrapper
 * transformLegacyResponse({ data: { id: 1, name: 'Test' } });
 * // { success: true, payload: { id: 1, name: 'Test' } }
 * 
 * // Direct data
 * transformLegacyResponse({ id: 1, name: 'Test' });
 * // { success: true, payload: { id: 1, name: 'Test' } }
 * ```
 */
export function transformLegacyResponse<T>(json: unknown): ApiSuccessResponse<T> {
  const obj = json as Record<string, unknown>;

  // Handle legacy paginated response: { items: [], total: N, ... }
  if (isLegacyPaginated(json)) {
    if (!IS_PRODUCTION) {
      console.debug('[ApiClient] 🔄 Transforming paginated response');
    }
    
    return {
      success: true,
      payload: json as T,
      message: 'Request successful',
      meta: {
        timestamp: new Date().toISOString(),
        total: obj.total as number | undefined,
        page: obj.page as number | undefined,
        limit: obj.limit as number | undefined,
      },
    };
  }

  // Handle legacy { data: T, success?, message? } response
  if (isLegacyDataWrapper(json)) {
    const legacy = json as LegacyDataResponse<T>;
    
    if (!IS_PRODUCTION) {
      console.debug('[ApiClient] 🔄 Transforming data wrapper response');
    }
    
    return {
      success: true,
      payload: legacy.data,
      message: legacy.message || 'Request successful',
      meta: {
        timestamp: new Date().toISOString(),
      },
    };
  }

  // Handle direct data response (no wrapper)
  if (!IS_PRODUCTION) {
    console.debug('[ApiClient] 🔄 Transforming direct data response');
  }
  
  return {
    success: true,
    payload: json as T,
    message: 'Request successful',
    meta: {
      timestamp: new Date().toISOString(),
    },
  };
}

/**
 * Auto-detect and transform response if needed
 * 
 * Convenience function that checks format and transforms if necessary.
 * 
 * @param json - Parsed JSON response
 * @returns Standardized ApiResponse (either original or transformed)
 * 
 * @example
 * ```typescript
 * const response = await fetch('/api/bookings');
 * const json = await response.json();
 * const standardized = autoTransformResponse<Booking[]>(json);
 * 
 * // Now always in format: { success: true/false, payload?: T, message?: string }
 * ```
 */
export function autoTransformResponse<T>(json: unknown): ApiResponse<T> {
  // Already in new format, return as-is
  if (isNewFormat(json)) {
    return json as ApiResponse<T>;
  }
  
  // Legacy format, transform it
  if (isLegacyResponse(json)) {
    return transformLegacyResponse<T>(json);
  }
  
  // Unknown format, wrap as success
  if (!IS_PRODUCTION) {
    console.warn(
      '[ApiClient] ⚠️ Unknown response format, wrapping as success',
      json
    );
  }
  
  return {
    success: true,
    payload: json as T,
    message: 'Request successful',
    meta: {
      timestamp: new Date().toISOString(),
    },
  };
}
